import { PermissionTypes } from '../components/menus/utils/generateSideMenuChildren'

/**
 * Vilken behörighet varje rutt kräver (#50).
 *
 * Menyn har alltid styrts av behörighet, men RUTTERNA har varit helt öppna — den som
 * kände till en URL kom fram till en sida som renderades tom (backend nekar med 403).
 * Kartan här är sanningen som `GuardedRoute` läser.
 *
 * NEKA SOM FÖRVAL: en rutt utan post här släpps inte igenom. Det gör att en ny rutt
 * fångas automatiskt i stället för att glida förbi oskyddad — det omvända kräver att
 * någon minns att lägga till vakten, vilket är precis den sortens sak som glöms.
 *
 * `mode` styr hur flera behörigheter tolkas:
 *   'every' (förval) — alla krävs
 *   'some'           — någon räcker
 *
 * Nyckeln är rutt-strängen EXAKT som den står i navigation.tsx.
 */

type Mode = 'every' | 'some'

interface RouteRule {
  required: string[]
  mode?: Mode
}

/** Rutter som alla inloggade når: startsida och auth-flödets landningssidor. */
export const OPEN_ROUTES = ['/app/start', '/app/callback', '/app/logout_redirect']

export const ROUTE_PERMISSIONS: Record<string, RouteRule> = {
  // --- Flaggor ---
  '/app/forebyggande-insatser': { required: [PermissionTypes.forebyggande_insatser] },
  '/app/utforare': { required: [PermissionTypes.utförare] },
  '/app/utforare/utforare=:utforare&from=:from&to=:to&flaglevel=:flaglevel&deviation=:deviation': { required: [PermissionTypes.utförare] },
  '/app/medarbetare': { required: [PermissionTypes.personal] },
  '/app/medarbetare/:id': { required: [PermissionTypes.personal] },
  '/app/brukare': { required: [PermissionTypes.brukare] },
  '/app/brukare/id=:id&namn=:namn': { required: [PermissionTypes.brukare] },
  '/app/arenden': { required: [PermissionTypes.uppföljningslista] },
  '/app/arenden/:id': { required: [PermissionTypes.uppföljningslista] },
  // `/app/nytt-arende` är borttagen (#30 PR 5) — ersatt av modalen på flagglistorna.
  // Ingen post här behövs: kartan nekar allt som inte står med.

  // Flagg-detaljvyn länkas från Brukare-, Personal- och Utförar-listorna samt
  // ärende-detaljen. Den som har NÅGON av vyerna ska kunna öppna flaggan — annars
  // slutar länkar fungera som gör det idag. Vakten stänger bara URL-bakvägen för
  // den som inte har någon av dem.
  '/app/avvikelse/flag=:flag&id=:id': {
    required: [PermissionTypes.brukare, PermissionTypes.personal, PermissionTypes.utförare, PermissionTypes.uppföljningslista],
    mode: 'some',
  },

  // Parkerad samlingsvy från när flaggor för handläggare planerades. Inga inlänkar
  // och ingen menypost — ligger kvar med personal som närmaste släkting (#50).
  '/app/handlaggare': { required: [PermissionTypes.personal] },
  '/app/handlaggare/:id': { required: [PermissionTypes.personal] },

  // --- Uppföljning/statistik och Benchmarking ---
  '/app/hemtjanst': { required: [PermissionTypes.uppföljning_statistik] },
  '/app/benchmarking': { required: [PermissionTypes.benchmarking] },
  '/app/benchmarking/introduction': { required: [PermissionTypes.benchmarking] },

  // --- Handläggning ---
  '/app/arendefordelning': { required: [PermissionTypes.ärendefördelning] },
  '/app/utanfor-teamstruktur': { required: [PermissionTypes.utfanför_teamstruktur] },
  '/app/mina-arenden': { required: [PermissionTypes.mina_ärenden] },
  '/app/volymuppfoljning': { required: [PermissionTypes.volymuppföljning] },
  '/app/nyligen-utganga': { required: [PermissionTypes.nyligen_utgångna] },
  '/app/beslutsstod': { required: [PermissionTypes.view_beslutsstöd] },
  '/app/beslutsstod/:id': { required: [PermissionTypes.view_beslutsstöd] },
  '/app/ibic-beslutsstod': { required: [PermissionTypes.view_ibic_beslutsstöd] },
  '/app/ibic-beslutsstod/config': { required: [PermissionTypes.edit_ibic_beslutsstöd] },
  '/app/beslutsstod-lss': { required: [PermissionTypes.view_lss_beslutsstöd] },
  '/app/malindex': { required: [PermissionTypes.view_målindex] },
  '/app/malindex/:id': { required: [PermissionTypes.view_målindex] },

  // --- Inställningar ---
  '/app/anvandare': { required: [PermissionTypes.användarhantering] },
  '/app/anvandare/ny': { required: [PermissionTypes.användarhantering] },
  '/app/anvandare/:id': { required: [PermissionTypes.användarhantering] },
  '/app/taggar': { required: [PermissionTypes.administration] },
  '/app/taggar/ny': { required: [PermissionTypes.administration] },
  '/app/taggar/:id': { required: [PermissionTypes.administration] },
  '/app/gäster': { required: [PermissionTypes.administration] },
  '/app/gäster/ny': { required: [PermissionTypes.administration] },
  '/app/gäster/:username': { required: [PermissionTypes.administration] },
  '/app/roller': { required: [PermissionTypes.administration] },
  '/app/roller/ny': { required: [PermissionTypes.administration] },
  '/app/roller/:name': { required: [PermissionTypes.administration] },
  '/app/övrigt': { required: [PermissionTypes.administration] },
  '/app/flaggkorning': { required: [PermissionTypes.flaggenerering] },
}

export const hasPermission = (required: string[], mode: Mode, permissions: string[]): boolean =>
  mode === 'some' ? required.some((r) => permissions.includes(r)) : required.every((r) => permissions.includes(r))

/** Avgör om en rutt får renderas. Saknad post i kartan = nekad (se ovan). */
export const isRouteAllowed = (path: string, permissions: string[]): boolean => {
  if (OPEN_ROUTES.includes(path)) return true

  const rule = ROUTE_PERMISSIONS[path]
  if (!rule) return false

  return hasPermission(rule.required, rule.mode ?? 'every', permissions)
}
